import React, { ReactNode, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { Tooltip } from "../../common/Tooltip"
import { useWindowSize } from "../../../hooks"
import { Typography } from "@mui/material"
import { Variant } from "@mui/material/styles/createTypography"

export enum TextVariant {
  bold = "bold",
  body2 = "body2",
  disclaimer = "disclaimer",
  link = "link",
  body1 = "body1",
}

enum SupportedMUIVariant {
  body2 = "body2",
  body1 = "body1",
  h1 = "h1",
  h2 = "h2",
  h3 = "h3",
  h4 = "h4",
}

export interface TextProps {
  variant: TextVariant
  children: ReactNode
  noWrap?: boolean
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  className?: string
  onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void
}

export interface VariantTextProps {
  children: ReactNode
  noWrap?: boolean
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  className?: string
  onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void
}

const bold = css`
  font-family: ${theme.font.bold};
`

const disclaimer = css`
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
  color: ${theme.color.black4};
`

const linkDisable = css`
  color: ${theme.color.grey2};
  font-family: ${theme.font.bold};
  text-decoration: none;
`

const link = css`
  color: ${theme.color.primary};
  font-family: ${theme.font.bold};
  cursor: pointer;
  transition: color 0.3s linear;
  text-decoration: none;

  &:visited {
    color: ${theme.color.primary};
  }

  &:hover {
    text-decoration: underline;
    color: ${theme.color.primaryDark};
  }
`

const overflowed = css`
  z-index: ${theme.zIndex.medium};
  position: relative;
`

const TextFactory = styled(Typography)<{
  $variant: "bold" | "body2" | "disclaimer" | "link" | "body1"
  $isOverflowed: boolean
}>`
  ${(p) => (p.$variant === "bold" ? bold : "")}
  ${(p) => (p.$variant === "disclaimer" ? disclaimer : "")}
  ${(p) => (p.$variant === "link" ? link : "")}
  ${(p) => (p.$isOverflowed ? overflowed : "")}
`

export const Text = (props: TextProps) => {
  const { variant, children, onClick, className, onMouseDown, ...rest } = props

  const [isOverflowed, setIsOverflow] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)
  const [windowWidth] = useWindowSize()

  function isOverflowActive(e: HTMLElement) {
    return e.offsetWidth < e.scrollWidth
  }

  useEffect(() => {
    if (textRef.current && isOverflowActive(textRef.current)) {
      setIsOverflow(true)
      return
    }

    setIsOverflow(false)
  }, [windowWidth, textRef.current?.offsetWidth, textRef.current?.scrollWidth])

  const getTypography = () => {
    if (Object.keys(SupportedMUIVariant).includes(variant)) {
      return (
        <TextFactory
          className={className}
          onClick={onClick}
          onMouseDown={onMouseDown}
          ref={textRef}
          children={children}
          $variant={variant}
          $isOverflowed={isOverflowed}
          variant={variant as Variant}
          {...rest}
        />
      )
    }

    return (
      <TextFactory
        className={className}
        onClick={onClick}
        onMouseDown={onMouseDown}
        ref={textRef}
        children={children}
        $variant={variant}
        $isOverflowed={isOverflowed}
        {...rest}
      />
    )
  }

  return (
    <Tooltip title={children ?? ""} disableHoverListener={!isOverflowed}>
      {getTypography()}
    </Tooltip>
  )
}

export const TextBold = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "bold",
}))``

export const TextSmall = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "body2",
}))``

export const TextDisclaimer = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "disclaimer",
}))``

export const TextLink = styled.span.attrs((props: TextProps) => ({
  ...props,
}))`
  ${(props) => (props.variant === "disclaimer" ? disclaimer : "")}
  ${(props) => (props.disabled ? linkDisable : link)}
`

export const TextRegular = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "body1",
}))``

export const Heading1 = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "h1",
}))``

export const Heading2 = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "h2",
}))``

export const Heading3 = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "h3",
}))``

export const Heading4 = styled(Text).attrs((props: VariantTextProps) => ({
  ...props,
  variant: "h4",
}))``
